import type { CityConfig } from "../city-template/types";

export const oxnardConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Oxnard",
  state: "California",
  stateAbbr: "CA",
  slug: "oxnard",
  route: "/cities/oxnard",
  title: "Emergency Passport Oxnard, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Oxnard, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Ventura County and coastal Southern California travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Oxnard, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Oxnard and Ventura County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Oxnard%20California%20harbor%20at%20golden%20hour%20with%20Channel%20Islands%20boats%20docked%2C%20strawberry%20fields%20in%20the%20foreground%2C%20coastal%20farmland%20meeting%20the%20Pacific%20Ocean%2C%20warm%20sunset%20light%20with%20amber%20and%20orange%20tones%2C%20Ventura%20County%20coastal%20landscape%2C%20professional%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20beautiful%20coastal%20agricultural%20scene&width=1800&height=700&seq=oxnard-hero-001&orientation=landscape",
  heroImageAlt: "Oxnard California harbor at golden hour with Channel Islands boats and coastal strawberry fields",
  geoPosition: "34.1975;-119.1771",
  geoPlacename: "Oxnard, California",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 60–75 minutes from Oxnard via US-101",
  },
  nearbyCitiesZips: [
    { name: "Oxnard", zips: "93030, 93033, 93035, 93036" },
    { name: "Ventura", zips: "93001, 93003, 93004" },
    { name: "Camarillo", zips: "93010, 93012" },
    { name: "Port Hueneme", zips: "93041, 93043" },
    { name: "Thousand Oaks", zips: "91320, 91360, 91361, 91362" },
    { name: "Simi Valley", zips: "93063, 93065" },
    { name: "Santa Paula", zips: "93060" },
    { name: "Fillmore", zips: "93015, 93016" },
    { name: "Moorpark", zips: "93021" },
    { name: "Newbury Park", zips: "91320" },
    { name: "Ojai", zips: "93023, 93024" },
    { name: "Westlake Village", zips: "91361, 91362" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Ventura County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "Ventura", route: "/cities/ventura" },
    { name: "Thousand Oaks", route: "/cities/thousand-oaks" },
    { name: "Simi Valley", route: "/cities/simi-valley" },
    { name: "Santa Barbara", route: "/cities/santa-barbara" },
    { name: "Burbank", route: "/cities/burbank" },
    { name: "Glendale", route: "/cities/glendale" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Oxnard remotely via secure courier. We do not have a walk-in office in Oxnard — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Ventura County travelers.",
  fpcOfficeAddress: "225 S Lake Ave, Pasadena, CA 91101",
  fpcOfficeDistance: "Serving all of Ventura County",
  serviceAreaIntro: "From the Oxnard Harbor to Downtown, from La Colonia to Riverpark — Fast Passport Center serves the entire Oxnard community. We regularly help travelers from Ventura, Camarillo, Port Hueneme, and Thousand Oaks get their passports fast.",
  nearbyCitiesIntro: "We serve all Oxnard neighborhoods and nearby Ventura County cities including Ventura, Camarillo, Port Hueneme, Santa Paula, Fillmore, and Moorpark. If you are in Ventura County, we have you covered.",
  howItWorksIntro: "Most Oxnard travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With Channel Islands Harbor and Ventura County's coastal communities serving as departure points for international travelers, Oxnard residents can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Oxnard and Ventura County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Oxnard? Here are the most common ones we hear from Ventura County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Ventura County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Carlos M.",
      location: "Oxnard, CA",
      rating: 5,
      text: "My cruise to Mexico was in 3 days and I realized my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved our family vacation.",
      service: "Emergency Passport",
    },
    {
      name: "Lisa R.",
      location: "Ventura, CA",
      rating: 5,
      text: "I needed to renew my passport for a business trip to London. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "James T.",
      location: "Camarillo, CA",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Greece. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Amanda S.",
      location: "Thousand Oaks, CA",
      rating: 5,
      text: "As a frequent international traveler from Ventura County, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "oxnard-1",
      name: "Oxnard Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1961 N C St",
      city: "Oxnard",
      state: "CA",
      zip: "93030",
      phone: "(805) 483-4431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Oxnard",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "oxnard-2",
      name: "Ventura Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "675 E Santa Clara St",
      city: "Ventura",
      state: "CA",
      zip: "93001",
      phone: "(805) 648-3341",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8.5 miles from Oxnard",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "oxnard-3",
      name: "Camarillo Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "303 N Lantana St",
      city: "Camarillo",
      state: "CA",
      zip: "93010",
      phone: "(805) 482-2641",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "10.2 miles from Oxnard",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for eastern Ventura County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "oxnard-4",
      name: "Thousand Oaks Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3435 E Thousand Oaks Blvd",
      city: "Thousand Oaks",
      state: "CA",
      zip: "91362",
      phone: "(805) 495-1331",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "18.5 miles from Oxnard",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Conejo Valley residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "oxnard-5",
      name: "Port Hueneme Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "155 E Ventura Rd",
      city: "Port Hueneme",
      state: "CA",
      zip: "93041",
      phone: "(805) 488-3453",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.5 miles from Oxnard",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for coastal Ventura County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "oxnard-6",
      name: "Ventura County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "800 S Victoria Ave",
      city: "Ventura",
      state: "CA",
      zip: "93009",
      phone: "(805) 654-2263",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "8.0 miles from Oxnard",
      website: "https://www.ventura.org/recordings/",
      websiteLabel: "Ventura County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Ventura County Coverage", agency: "No Ventura County expertise", fpc: "Oxnard Harbor, Channel Islands, and Ventura County coastal travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Ventura County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Ventura County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Ventura County applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Ventura County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Ventura County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Oxnard." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Oxnard address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Oxnard before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Oxnard for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Ventura County travelers." },
    { q: "How fast can I renew my passport in Oxnard?", a: "Fast Passport Center offers fast passport renewal in Oxnard in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Oxnard in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Oxnard?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Oxnard residents." },
    { q: "Can I expedite a child passport in Oxnard?", a: "Yes. Fast Passport Center expedites child passports in Oxnard for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Oxnard with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Oxnard?",
      a: "With Fast Passport Center's emergency courier service, Oxnard travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency downtown?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Oxnard?",
      a: "Popular options include the Oxnard Post Office (93030), Ventura Main Post Office (93001), Camarillo Post Office (93010), and Port Hueneme Post Office (93041). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Oxnard?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in nearby Ventura County communities?",
      a: "We serve all of Ventura County including Ventura, Camarillo, Santa Paula, Fillmore, Moorpark, and Ojai. Our overnight shipping labels work from any UPS location, so your location within Ventura County doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Oxnard?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Oxnard?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Oxnard and Ventura offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Oxnard?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Oxnard?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Ventura County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Oxnard", address: "2001 N Oxnard Blvd, Oxnard, CA 93036", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Ventura", address: "1750 E Main St, Ventura, CA 93001", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Oxnard", address: "1901 N Ventura Rd, Oxnard, CA 93036", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Camarillo", address: "2151 E Daily Dr, Camarillo, CA 93010", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Oxnard", address: "2701 E Vineyard Ave, Oxnard, CA 93036", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Thousand Oaks", address: "2200 E Thousand Oaks Blvd, Thousand Oaks, CA 91362", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Ventura", address: "4703 Telephone Rd, Ventura, CA 93003", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Camarillo", address: "500 E Ventura Blvd, Camarillo, CA 93010", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Oxnard",
      address: "4050 Saviers Rd, Oxnard, CA 93033",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Ventura",
      address: "2001 E Gonzales Rd, Oxnard, CA 93036",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Camarillo",
      address: "1300 E Ventura Blvd, Camarillo, CA 93010",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Thousand Oaks",
      address: "1810 E Avenida De Los Arboles, Thousand Oaks, CA 91362",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Conejo Valley residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "OXR",
      name: "Oxnard Airport",
      distance: "4 miles from downtown Oxnard",
      address: "2889 W 5th St, Oxnard, CA 93030",
      note: "Regional airport serving general aviation and some charter flights. No commercial airline service.",
      flightUrl: "https://www.oxnardairport.org/",
      flightLabel: "Oxnard Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "55 miles from Oxnard",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via US-101.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "BUR",
      name: "Hollywood Burbank Airport",
      distance: "45 miles from Oxnard",
      address: "2627 N Hollywood Way, Burbank, CA 91505",
      note: "Convenient alternative for domestic flights and some international connections. Accessible via US-101.",
      flightUrl: "https://www.hollywoodburbankairport.com/",
      flightLabel: "Burbank Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the California Department of Public Health Vital Records. Online, mail, or in-person options available for Ventura County residents. Processing typically takes 5–10 business days.",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through California CDPH Vital Records. Required for estate and legal matters in Ventura County.",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the Ventura County Clerk-Recorder. Certified copies available for name changes and legal purposes in Oxnard and throughout Ventura County.",
      url: "https://www.ventura.org/recordings/",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Ventura County Superior Court. Required for name change passport corrections and legal documentation.",
      url: "https://www.ventura.courts.ca.gov/",
    },
  ],
};